export interface LoreArticle {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  date: string;
  readTime: string;
  views: string;
  cover: string;
  coverAlt: string;
  tag: string;
  featured?: boolean;
  content: {
    intro: string;
    sections: { heading: string; body: string[]; sub?: { heading: string; body: string[] }[] }[];
    conclusion: string;
  };
}

export const loreCategories = [
  { key: 'all', label: '全部设定' },
  { key: 'worldview', label: '世界观' },
  { key: 'character', label: '角色与来历' },
  { key: 'story', label: '故事背景' },
  { key: 'deep', label: '深度解读' },
];

export const loreArticles: LoreArticle[] = [
  {
    slug: 'zahuopu-laishi',
    title: '洞洞杂货铺是什么？从零认识这家神秘小店',
    category: 'character',
    excerpt: '一家只对有缘人敞开大门的小店，货架上没有一件完整的物品——每一件都留着一个小小的“洞”。洞洞杂货铺究竟卖什么？它又从何而来？',
    date: '2026-08-10',
    readTime: '8 分钟',
    views: '3.2万',
    tag: '入门必读',
    featured: true,
    cover:
      'https://readdy.ai/api/search-image?query=cozy%20mysterious%20old%20grocery%20shop%20interior%20with%20wooden%20shelves%2C%20warm%20amber%20lantern%20light%2C%20dusty%20antique%20objects%2C%20each%20item%20with%20a%20small%20round%20hole%2C%20whimsical%20nostalgic%20atmosphere%2C%20soft%20cream%20and%20terracotta%20tones%2C%20painterly%20illustration%20style%2C%20detailed%20and%20magical&width=900&height=620&seq=zahuopu-laishi-1&orientation=landscape',
    coverAlt: '洞洞杂货铺神秘小店内部场景',
    content: {
      intro:
        '在多数读者的第一印象里，洞洞杂货铺是一家“什么都缺一件”的小店。货架上的陶罐缺了底、怀表缺了指针、信笺缺了一角。可奇怪的是，每一位推开木门的客人，都能在这里找到自己正缺的那件东西。',
      sections: [
        {
          heading: '一、它卖的不是商品，而是“空缺”',
          body: [
            '洞洞杂货铺最核心的设定，是“洞”并非破损，而是空缺的具象化。每一个洞，都对应着某个人生命里正缺失、或即将缺失的一部分——一句没来得及说的话、一段被遗忘的记忆、一份悬而未决的心愿。',
            '店主从不标价。客人进门时，会在掌心出现一个淡淡的印记，印着他们真正需要的“洞”的名字。交易的方式，永远是一桩等价交换：带走一个空缺，就得留下一段属于自己的、同样重要的空缺。',
          ],
          sub: [
            {
              heading: '“洞洞”二字从何而来',
              body: [
                '“洞洞”取的是洞隙的重复与强调，既是货架上那些实实在在的孔洞，也暗指命运里层层叠叠的缺口。作者在访谈中曾透露，这个名字来自童年听过的“补漏”传说——传说中有人专门修补世间遗漏的遗憾，而洞洞杂货铺，正是这些传说在现代都市里的栖身之所。',
              ],
            },
          ],
        },
        {
          heading: '二、小店坐落在哪里',
          body: [
            '洞洞杂货铺没有固定地址。它出现在城市里那些被遗忘的街角——拆除到一半的老巷、凌晨的旧商场、暴雨后忽然出现的门面。只有“正在缺一样东西”的人，才会在某个拐角遇见它。',
            '门外挂着一盏永不熄灭的纸灯笼，门上嵌着一颗小小的、会旋转的木珠。推门那一下，木珠会转向属于你的方向，店内的布局也随之改变——每个人看到的洞洞杂货铺，都不是同一家。',
          ],
        },
        {
          heading: '三、读这部漫画该带着什么心态',
          body: [
            '洞洞杂货铺不是传统的冒险或悬疑漫画，而是一部以“人情与缺口”为核心的单元剧。每一话通常独立成篇，讲述一位客人、一件商品、一个空缺之间的交换与和解。',
            '作者擅长用温暖而略带哀愁的笔触，把遗憾、思念、放手这些抽象情绪，装进一件件具体的小物件里。阅读时放慢节奏，反而更能品出其中的韵味。',
          ],
        },
      ],
      conclusion:
        '如果你正缺一样说不清道不明的东西，不妨在下一个拐角，留意那盏晃动的纸灯笼。洞洞杂货铺的答案，从来都藏在你自己留下的那个“洞”里。',
    },
  },
  {
    slug: 'shijieguan-shezhi',
    title: '世界观全景解析：规则、边界与隐藏秩序',
    category: 'worldview',
    excerpt: '洞洞杂货铺并非孤立存在，它背后有一套隐秘而自洽的世界规则。本文为你梳理杂货铺与世界之间的边界、交易铁律与隐藏秩序。',
    date: '2026-08-06',
    readTime: '12 分钟',
    views: '2.7万',
    tag: '深度解读',
    cover:
      'https://readdy.ai/api/search-image?query=aerial%20view%20of%20a%20mystical%20old%20town%20at%20dusk%2C%20winding%20alleyways%20with%20warm%20lantern%20lights%2C%20hidden%20tiny%20shop%20doors%20appearing%20between%20buildings%2C%20layered%20map%20like%20composition%2C%20deep%20moss%20green%20and%20amber%20terracotta%20palette%2C%20nostalgic%20magical%20illustration%2C%20dreamy%20atmosphere&width=900&height=620&seq=worldview-shezhi-1&orientation=landscape',
    coverAlt: '洞洞杂货铺世界观中的隐秘小镇',
    content: {
      intro:
        '洞洞杂货铺的世界并非现实世界的简单翻版。作者用大量留白与细节，构建了一套藏在日常之下的隐秘秩序。读懂这套世界观，才能理解每一桩交易背后真正的分量。',
      sections: [
        {
          heading: '一、三层世界的结构',
          body: [
            '整部作品的世界观可以粗略划分为三层：最外层是我们熟悉的现实都市；中间是“记忆缝隙”，那些被遗忘的街巷、废弃的站台与空置的房屋；最内层，则是洞洞杂货铺本身所在的“库房”，一个存放着所有空缺的异空间。',
            '杂货铺正是这三层世界的交汇点。它从现实里收集客人，在记忆缝隙里往返，把一切交换都引向最内层的库房完成。',
          ],
        },
        {
          heading: '二、交易的三条铁律',
          body: [
            '第一，交易必须出于“自愿”。店主可以引导、可以等待，却从不替客人做决定，任何被强塞的“空缺”都会原样退回。',
            '第二，一洞换一洞。带走一件商品的同时，客人必须留下一段属于自己的空缺，这份空缺会成为下一位客人的缘分。',
            '第三，记忆不可伪造。交易中涉及的记忆可以交换、可以封存，但绝不能凭空捏造——捏造的空缺会让整个库房失衡，这也是全作最核心的冲突来源。',
          ],
          sub: [
            {
              heading: '库房失衡意味着什么',
              body: [
                '当有人的空缺被强行填补、或有人试图绕过等价交换，库房里的“洞”就会开始共鸣、相互吞噬。这既是漫画面上的灾难，也是角色成长与和解的契机。',
              ],
            },
          ],
        },
        {
          heading: '三、那些藏在细节里的规则',
          body: [
            '纸灯笼的火苗会随着库存变化摇曳；木珠的转向暗示客人的来意；墙上的裂缝越多，说明这座城市被遗忘的东西越多。作者把这些设定藏在画面背景里，重看时常常会有新发现。',
            '理解这些隐藏秩序，会让你在后续剧情里获得更完整的阅读体验——这正是《洞洞杂货铺》作为一部世界观作品最有魅力的地方。',
          ],
        },
      ],
      conclusion:
        '洞洞杂货铺的世界观并不复杂，却足够自洽。它用“空缺”与“交换”这两个朴素概念，撑起了一座既有温度、又有深度的情感宇宙。',
    },
  },
  {
    slug: 'dongdong-hanyi',
    title: '“洞洞”到底是什么意思？三个层面的完整解读',
    category: 'deep',
    excerpt: '“洞洞”二字看似童稚，实则蕴含全作最深层的主题。从字面、象征到哲学，本文为你一层层剥开“洞洞”的全部含义。',
    date: '2026-07-28',
    readTime: '10 分钟',
    views: '4.1万',
    tag: '热门',
    featured: true,
    cover:
      'https://readdy.ai/api/search-image?query=close%20up%20of%20hands%20holding%20a%20small%20object%20with%20a%20perfect%20round%20hole%20in%20the%20center%2C%20warm%20window%20light%2C%20dust%20particles%20floating%2C%20soft%20cream%20background%2C%20thoughtful%20contemplative%20mood%2C%20nostalgic%20painterly%20illustration%2C%20amber%20and%20moss%20tones&width=900&height=620&seq=dongdong-hanyi-1&orientation=landscape',
    coverAlt: '掌心中带有一个圆洞的神秘物件',
    content: {
      intro:
        '“洞洞”是全作标题的核心词，也是最容易被轻轻略过的两个字。可事实上，整部漫画的主题、情感与哲学，都凝结在这两个重复的字眼里。',
      sections: [
        {
          heading: '一、字面层：那些看得见的孔洞',
          body: [
            '最直接的一层，就是货架上一件件缺了口的商品。这些看得见的洞，是读者进入这个世界的第一扇门，也是“洞洞”最朴素的来源。',
            '作者用大量特写描绘这些孔洞的形状、质感与边缘——圆润的、锋利的、正在愈合的。每一个洞都像一句话，等待着被读懂。',
          ],
        },
        {
          heading: '二、象征层：空缺与遗憾',
          body: [
            '在象征层面，“洞洞”代表着生命中一切尚未圆满的部分：没说出口的话、没告别的故人、没做完的梦。它们不一定是痛苦，更多时候只是“还在那里”的缺口。',
            '故事反复告诉读者：人有缺口并不可耻。真正重要的，是面对自己的“洞”，并决定用它交换什么。',
          ],
        },
        {
          heading: '三、哲学层：洞的另一面是光',
          body: [
            '第三层，也最动人。“洞”本身并非虚空——它同时是接纳与释放的通道。当一段空缺被诚实交换，当一句遗憾终于说出口，那个洞就会透出光来。',
            '正如漫画中的一句台词：“不要害怕身上的洞，那往往是光会照进来的地方。”这既是全作的精神内核，也是“洞洞”二字最完整的注解。',
          ],
        },
      ],
      conclusion:
        '“洞洞”既具体又抽象，既是画面的要素，也是全作的灵魂。读懂这两个字，就读懂了大半部《洞洞杂货铺》。',
    },
  },
  {
    slug: 'dianzhu-mimi',
    title: '店主的来历：她为什么会守着一家洞洞杂货铺',
    category: 'character',
    excerpt: '那位总坐在柜台后、温声细语的女店主，身上藏着全作最大的谜题之一。她的来历、她与库房的关系，以及她守在这里的真正原因。',
    date: '2026-07-18',
    readTime: '9 分钟',
    views: '2.1万',
    tag: '角色',
    cover:
      'https://readdy.ai/api/search-image?query=portrait%20of%20a%20calm%20mysterious%20woman%20shopkeeper%20in%20a%20traditional%20long%20coat%20sitting%20behind%20an%20old%20wooden%20counter%2C%20warm%20lantern%20glow%2C%20paper%20lanterns%20around%2C%20gentle%20sad%20smile%2C%20nostalgic%20illustration%2C%20cream%20and%20terracotta%20palette%2C%20detailed%20painterly%20style&width=900&height=620&seq=dianzhu-mimi-1&orientation=landscape',
    coverAlt: '柜台后温声细语的神秘女店主',
    content: {
      intro:
        '每一位踏进洞洞杂货铺的客人，都会先被柜台后那位女店主吸引。她不急不躁，眼神里藏着许多往事。而她的来历，正是全作最扣人心弦的暗线。',
      sections: [
        {
          heading: '一、她与库房的约定',
          body: [
            '据目前线索，店主并非天生守护杂货铺。她年轻时曾是一位失去重要之物的客人，在与上一任店主交易时，选择留下自己整段与“家”有关的记忆，换回了一样再也无法挽回的东西。',
            '这份交换让她得以看清洞的真相，也让她与库房结下了契约——从此，她成为了洞洞杂货铺的守门人。',
          ],
        },
        {
          heading: '二、她记得一切，唯独忘了自己',
          body: [
            '店主拥有过人的洞察力，能一眼看穿客人缺的是什么。但讽刺的是，正是因为她交出了自己的记忆，她始终想不起自己最初留下的那个“洞”究竟装着什么。',
            '这一设定让她既是旁观者，又是等待被治愈的人。每当她帮助一位客人完成和解，她离找回自己，就更近一步。',
          ],
        },
        {
          heading: '三、守在这里的原因',
          body: [
            '表面上，她守店是为了履行与库房的约定。但更深一层，她是在等——等一位能帮她补全自我的人，也等那个被她亲手封存的答案。',
            '这也解释了为何她对每一位客人都如此耐心：在她眼里，每一次交易，都是对她自己空缺的一次温柔回望。',
          ],
        },
      ],
      conclusion:
        '店主的秘密，是贯穿全作的温柔悬念。随着主线推进，她那被遗忘的“洞”正在一点点被揭开——而这，或许也是每一位读者最期待揭晓的谜底。',
    },
  },
  {
    slug: 'zhuti-gushi',
    title: '单元剧背后的主线：每一条温柔支线都在指向什么',
    category: 'story',
    excerpt: '《洞洞杂货铺》以单元剧见长，但那些看似独立的支线故事，其实正围绕一条隐藏主线层层收拢。本文为你拆解这条主线。',
    date: '2026-07-08',
    readTime: '7 分钟',
    views: '1.6万',
    tag: '故事线',
    cover:
      'https://readdy.ai/api/search-image?query=rows%20of%20mysterious%20small%20objects%20arranged%20on%20shelves%20like%20a%20trail%2C%20each%20glowing%20faintly%2C%20leading%20the%20eye%20to%20a%20distant%20warm%20light%2C%20dark%20cozy%20background%2C%20moss%20green%20and%20amber%20tones%2C%20narrative%20painterly%20illustration%2C%20mysterious%20and%20hopeful&width=900&height=620&seq=zhuti-gushi-1&orientation=landscape',
    coverAlt: '货架上发光的物件组成的叙事线索',
    content: {
      intro:
        '很多人把《洞洞杂货铺》当成一部轻松治愈的单元剧来读，但细细品味会发现，每一话看似独立，其实都在为主线的揭晓悄悄铺垫。',
      sections: [
        {
          heading: '一、单元剧与主线的结构',
          body: [
            '作品采用“单元剧 + 暗线”的双层结构。每一话解决一位客人的空缺，同时也向读者抛出店主身份、库房起源等谜团的小碎片。',
            '这种结构让读者既能享受单话的完整治愈，又始终保有一种追着主线走下去的期待。',
          ],
        },
        {
          heading: '二、反复出现的意象',
          body: [
            '留意那些反复出现的意象：旋转的木珠、墙角的裂缝、那盏永不熄灭的纸灯笼。它们每一次出现，都在不动声色地推进主线的进度条。',
            '尤其那位反复登场、总在深夜独自徘徊的神秘客人，几乎可以断定他与店主的过去有直接关联。',
          ],
        },
        {
          heading: '三、作者埋下的叙事野心',
          body: [
            '从访谈与彩页来看，作者显然不满足于做一部单纯的治愈小品。单元剧是入口，主线才是目的地——它关乎“空缺的起源”，也关乎洞洞杂货铺自身存在的意义。',
            '对于追更中的读者来说，最好的方式，是既享受每一话的温柔，也留意那些被作者轻轻放在角落的线索。',
          ],
        },
      ],
      conclusion:
        '《洞洞杂货铺》的表面是治愈，内里是一场精心布局的叙事。那些温柔支线，最终都将汇入同一条涌动的暗流。',
    },
  },
  {
    slug: 'yuedu-zhinan',
    title: '新人入坑指南：从第一话读到最新话的完整路线',
    category: 'story',
    excerpt: '刚接触《洞洞杂货铺》不知从哪读起？本文给出从第一话到最新话的阅读路线、必读单话清单与追更建议。',
    date: '2026-06-26',
    readTime: '6 分钟',
    views: '5.8万',
    tag: '新手必备',
    featured: true,
    cover:
      'https://readdy.ai/api/search-image?query=an%20open%20old%20book%20with%20a%20tiny%20glowing%20shop%20door%20emerging%20from%20its%20pages%2C%20reading%20glasses%20and%20paper%20lantern%20nearby%2C%20warm%20cozy%20desk%20light%2C%20cream%20and%20amber%20tones%2C%20nostalgic%20magical%20illustration%2C%20inviting%20and%20warm&width=900&height=620&seq=yuedu-zhinan-1&orientation=landscape',
    coverAlt: '摊开的旧书页中浮现出杂货铺的大门',
    content: {
      intro:
        '如果你是刚听说洞洞杂货铺、还在犹豫要不要入坑的新读者，这份指南可以帮你快速找到最适合的阅读方式，少走弯路。',
      sections: [
        {
          heading: '一、从哪里开始读',
          body: [
            '建议从第一话开始按顺序阅读。虽然单话可独立阅读，但作者在早期就埋下了世界观与店主的伏笔，按序阅读能获得最完整的体验。',
            '如果你更在意整体氛围而非剧情，也可以先挑几话公认的“神回”感受风格，再回头补齐。',
          ],
        },
        {
          heading: '二、必读的几话',
          body: [
            '《洞洞》首话、《失去指针的怀表》《纸灯笼的来历》等几话，被读者公认为最适合入门的篇章。它们短小完整，又恰好展示了作品的核心魅力。',
            '读到中段后，建议特别留意店主出场较多、以及那位神秘客人登场的话数，它们对理解主线至关重要。',
          ],
        },
        {
          heading: '三、追更建议',
          body: [
            '作品更新相对稳定，适合作为轻松的日常追更。若想一口气看完，等待一个长假期集中阅读会更有沉浸感。',
            '无论选择哪种方式，记得把节奏放慢——洞洞杂货铺的故事，值得被细细咀嚼。',
          ],
        },
      ],
      conclusion:
        '入坑《洞洞杂货铺》没有门槛，只要愿意放慢脚步、用心体会每一段空缺与和解，你就已经找到了正确的大门。',
    },
  },
];

export function getArticleBySlug(slug: string): LoreArticle | undefined {
  return loreArticles.find((a) => a.slug === slug);
}

export function getFeaturedArticles(): LoreArticle[] {
  return loreArticles.filter((a) => a.featured);
}

export function getRelatedArticles(slug: string, count = 3): LoreArticle[] {
  const current = getArticleBySlug(slug);
  const others = loreArticles.filter((a) => a.slug !== slug);
  if (!current) return others.slice(0, count);
  const sameCat = others.filter((a) => a.category === current.category);
  const rest = others.filter((a) => a.category !== current.category);
  return [...sameCat, ...rest].slice(0, count);
}